import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";

import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import { showToast } from "../../../../store/actions";
import { useCronReducer } from "../../../../util/cron";
import WorkspaceService from "../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { FormTile } from "../../../Common/Tile";
import ObjectPanel from "./ObjectPanel";
import { setPrevState } from "../../../../util/common";
import CommonLayout from "../../../Common/Layout";
import DataSource from "../../../../service/dataSource";
import { regexName } from "../../../../util/regularExpress";
import { OBJECT_TYPE } from "../../../../util/Data/DataModeling";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../utils/JobData";
import { navScheduledJobs } from "../../../kmk/AppData";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import NextJobForm from "../utils/NextJobForm";
import Scheduler from "../utils/Scheduler";
import DataPipelineService from "../../../../service/dataPipelineService";
import SmallToolbar from "../../../Common/SmallToolbar";
import CloneJob from "../CloneJob";
import UnloadForm from "../utils/UnloadForm";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
}));

const ComputeForm = memo(({ cluster, setComputeState }) => {
  const clusterList = useSelector((state) => state.clusterList);

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
          <InputLabel>Compute Cluster</InputLabel>
          <Select
            value={cluster}
            onChange={(e) => setComputeState(e.target.value)}
            label="Compute Cluster"
            MenuProps={{ sx: { height: "220px" } }}
          >
            {(clusterList || [])?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
});

// Form component to display Add Job Name Field Section
const AddJobForm = ({ jobState, setJobState, dataPipelineList, confirmPipelineChange, textRef, Job_id }) => {
  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setJobState, { [dataState]: str });
  };

  return (
    <Grid container direction={"row"} spacing={2}>
      <Grid>
        <TextField
          value={jobState.name}
          sx={{ width: { xs: "100%", sm: "300px" } }}
          required
          label="Job Name"
          inputProps={{ maxLength: 30 }}
          onChange={(e) => handleJobName(e.target.value, "name")}
        />
      </Grid>
      <Grid>
        <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} required>
          <InputLabel>Pipeline</InputLabel>
          <Select
            value={jobState.DataPipeline_id}
            onChange={confirmPipelineChange}
            label="Pipeline"
            disabled={!!Job_id}
            MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
            renderValue={(selected) => {
              const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
              const isOverflow = (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Tooltip placement="bottom" title={selectedPipeline?.Name} arrow disableHoverListener={!isOverflow}>
                    <Typography
                      variant="body1"
                      ref={textRef}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}
                    >
                      {selectedPipeline?.Name || "Select a pipeline"}
                    </Typography>
                  </Tooltip>
                  {selectedPipeline?.IsDefault === 1 && (
                    <Tooltip placement="right" title="Default Pipeline" arrow>
                      <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                    </Tooltip>
                  )}
                </Stack>
              );
            }}
          >
            {dataPipelineList.map((p) => (
              <MenuItem
                key={p.DataPipeline_id}
                value={p.DataPipeline_id}
                style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                    {p.Name}
                  </Typography>
                  {p.IsDefault === 1 && (
                    <Tooltip placement="right" title="Default Pipeline" arrow>
                      <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                    </Tooltip>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid sx={{ display: "flex", alignItems: "center" }}>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox />}
            value={jobState.isEntryJob}
            checked={jobState.isEntryJob}
            onChange={() => setJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
            label="Set as the entry point for the pipeline"
          />
        </FormGroup>
      </Grid>
    </Grid>
  );
};

export default function MDMScheduler() {
  const { Workspace_id, Job_id } = useParams();
  const workspace = useSelector((state) => state.workspace);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled } = workspace.workspace;
  const defaultCluster = config.defaultCluster;

  const [initialLoading, setInitialLoading] = useState(true);
  const [jobState, setJobState] = useState({
    name: "",
    jobType: "",
    timeZone: "",
    nextRunTime: "",
    loading: false,
    scheduleEnabled: false,
    type: 5,
    DataPipeline_id: "",
    isEntryJob: false,
  });

  const [unloadState, setUnloadState] = useState({
    unloadPath: "",
    location: "",
    unloadEnabled: false,
  });

  const [computeState, setComputeState] = useState({
    cluster: defaultCluster,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [openClone, setOpenClone] = useState(false);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [dataPipelineList, setDataPipelineList] = useState([]);

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const textRef = useRef(null);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer(DETAUL_CRON_VALUE);

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCancel = () => {
    setPrevState(setDeleteJobState, { openConfirm: false });
  };

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const deleteJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteMDMJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const handleSave = async (e, clone = false) => {
    setPrevState(setJobState, { loading: true });

    const selectedEntities = entities.filter((source) => source.checked);
    const selectAll = selectedEntities.length === entities.length;

    const submitData = {
      name: jobState.name,
      type: jobState.type,
      DataPipeline_id: jobState.DataPipeline_id,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: jobState.scheduleEnabled || false,
      cluster: computeState.cluster,
      Stage_id: unloadState.location,
      unloadEnabled: unloadState.unloadEnabled || false,
      unloadPath: unloadState.unloadPath || "",
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      entity: { selectAll, entities: selectAll ? [] : selectedEntities.map(({ name }) => name) },
      isEntryJob: jobState.isEntryJob,
    };

    const wsService = new WorkspaceService();
    const response = Job_id
      ? await wsService.updateDataModelingJob(submitData, Workspace_id, Job_id, 5)
      : await wsService.createDataModelingJob(submitData, Workspace_id, 5);

    if (response.success) {
      if (clone) {
        setPrevState(setJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const getEntities = useCallback(async () => {
    const datasource = new DataSource();

    const { success, data } = await datasource.getObjectNamesByType(
      Workspace_id,
      OBJECT_TYPE.Entity,
      JOB_BY_TYPE.dataMastering,
      Job_id
    );

    return success ? data : [];
  }, [Job_id, Workspace_id]);

  useEffect(() => {
    const getJobSchedule = async () => {
      const wsService = new WorkspaceService();
      const response = await wsService.getMDMSchedule(Workspace_id, Job_id);
      if (response === "Unauthorized" || !response.success)
        return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      setPrevState(setJobState, {
        name: response.data.Name,
        jobType: response.data.Detail?.type,
        scheduleEnabled: response.data.Enabled,
        timeZone: response.data.ServerTimeZone,
        nextRunTime: response.data.nextRun,
        DataPipeline_id: response.data.DataPipeline_id,
      });

      setPrevState(setUnloadState, {
        unloadPath: response.data.Detail.unloadPath,
        unloadEnabled: response.data.Detail.unloadEnabled,
        location: response.data.Detail.Stage_id || "",
      });

      setPrevState(setNextJobState, {
        nextDataPipelineId: response.data.NextPipeline_id || "",
        nextJobId: response.data.NextJob_id || nextJobIdRef.current,
        errorDataPipelineId: response.data.ErrorPipeline_id || "",
        errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
        nextJobToggle: response.data.enableNextJob || false,
      });

      dispatchValues({
        type: "set_values",
        value: response.data.Schedule,
      });

      return response.data;
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    (async () => {
      if (clusterList === false) return;
      setInitialLoading(true);
      setEntityLoading(true);
      setPrevState(setJobState, { loading: true });

      let jobDetails = {};
      if (Job_id) {
        jobDetails = await getJobSchedule();
      }
      if (!jobDetails) return;

      let dataPipelines = await getDataPipelineList();
      if (!jobDetails.DataPipeline_id)
        setPrevState(setJobState, {
          DataPipeline_id: dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id,
        });
      setDataPipelineList(dataPipelines);

      setInitialLoading(false);

      //Other details
      const entities = await getEntities();

      //Setting selected cluster
      if (Job_id && clusterList?.length)
        setPrevState(setComputeState, {
          cluster: clusterList.some((cluster) => cluster.value === jobDetails.Detail.Cluster)
            ? jobDetails.Detail.Cluster
            : defaultCluster,
        });

      //Setting selected refresh entities
      if (entities.length) {
        if (jobDetails?.DataSource?.selectAll === true)
          setEntities(entities.map((item) => ({ ...item, checked: true })));
        else {
          const selectedEntities = jobDetails?.DataSource?.entities || [];
          setEntities(entities.map((item) => ({ ...item, checked: !Job_id || selectedEntities.includes(item.name) })));
        }
      }

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(jobDetails.DataPipeline_id)
        );
        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setEntityLoading(false);
      setPrevState(setJobState, { loading: false, isEntryJob });
    })();
  }, [Job_id, Workspace_id, jobState.type, navigate, dispatchValues, defaultCluster, getEntities, clusterList]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`, displayName: "Data Pipelines", active: false },
    { link: "#", displayName: Job_id ? "Edit Data Mastering Job" : "Add Data Mastering Job", active: true },
  ];

  const disableSave = useMemo(() => {
    return (
      !(!unloadState.unloadEnabled || (unloadState.unloadEnabled && unloadState.location && unloadState.unloadPath)) ||
      jobState.name === "" ||
      jobState.loading ||
      computeState.cluster === "" ||
      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)) ||
      (isDataVaultEnabled && entities.every((source) => !source.checked))
    );
  }, [
    unloadState.unloadEnabled,
    unloadState.location,
    unloadState.unloadPath,
    jobState.name,
    jobState.loading,
    computeState.cluster,
    nextJobState.nextJobToggle,
    nextJobState.nextJobId,
    nextJobState.errorJobId,
    isDataVaultEnabled,
    entities,
  ]);

  const setCluster = useCallback((cluster) => {
    setPrevState(setComputeState, { cluster });
  }, []);

  const toggleSchedule = useCallback(
    () => setJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={jobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          overflowX: "hidden",
          overflowY: "auto",
          paddingBottom: "20px",
        }}
        id="main_box"
      >
        {initialLoading ? (
          <CircularProgress
            color="primary"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          <>
            <Backdrop
              sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={jobState.loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
            <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
              <Box className={classes.subContainer}>
                <Box className={classes.PanelContainer}>
                  <Grid container direction="row" sx={{ mb: 4 }}>
                    <Grid sx={{ mb: 1 }} size={12}>
                      {Job_id ? (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Edit Data Mastering Job
                        </Typography>
                      ) : (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Add Data Mastering Job
                        </Typography>
                      )}
                      <ButtonUI
                        onClick={handleSave}
                        variant="contained"
                        color="success"
                        disabled={disableSave}
                        sx={{ mb: "5px" }}
                      >
                        Save
                      </ButtonUI>
                      <ButtonUI
                        onClick={handleCancelClick}
                        variant="contained"
                        color="secondary"
                        sx={{ mb: "5px", ml: "15px" }}
                      >
                        Cancel
                      </ButtonUI>

                      {Job_id ? (
                        <>
                          <ButtonUI
                            onClick={() => setOpenClone(true)}
                            variant="contained"
                            color="info"
                            sx={{ mb: "5px", mx: "15px" }}
                            disabled={disableSave}
                          >
                            Clone
                          </ButtonUI>
                          <ButtonUI
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setPrevState(setDeleteJobState, {
                                openConfirm: true,
                                confirmTitle: "Delete Data Mastering Job",
                                confirmMessage: `Are you sure you want to delete "${jobState.name}" Job?"`,
                              });
                            }}
                            disabled={!Job_id}
                            sx={{ mb: "5px" }}
                          >
                            Delete
                          </ButtonUI>
                        </>
                      ) : (
                        <></>
                      )}
                      <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                        <HelpContextIcon screen="Data Mastering Job" />
                      </Box>
                    </Grid>
                    <Grid className={classes.BigTileContainer} size={12}>
                      <FormTile>
                        <AddJobForm
                          jobState={jobState}
                          setJobState={setJobState}
                          dataPipelineList={dataPipelineList}
                          confirmPipelineChange={confirmPipelineChange}
                          textRef={textRef}
                          Job_id={Job_id}
                        />
                      </FormTile>
                    </Grid>
                    <Grid className={classes.BigTileContainer} size={12}>
                      <SmallToolbar>Schedule</SmallToolbar>
                      <FormTile
                        style={{
                          overflowX: "auto",
                        }}
                      >
                        <Scheduler
                          scheduleEnabled={jobState.scheduleEnabled}
                          cronValue={values.cronValue}
                          dispatchValues={dispatchValues}
                          toggleValue={toggleSchedule}
                          timeZone={jobState.timeZone}
                          nextRunTime={jobState.nextRunTime}
                        />
                      </FormTile>
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <ObjectPanel
                        objectType={OBJECT_TYPE.Entity}
                        objects={entities}
                        setObjects={setEntities}
                        loading={entityLoading}
                        setLoading={setEntityLoading}
                        getObjects={getEntities}
                      />
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Compute Clusters</SmallToolbar>
                      <FormTile>
                        <ComputeForm cluster={computeState.cluster} setComputeState={setCluster} />
                      </FormTile>
                    </Grid>
                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Error Report</SmallToolbar>
                      <FormTile>
                        <UnloadForm unloadState={unloadState} setUnloadState={setUnloadState} />
                      </FormTile>
                    </Grid>
                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Next Job</SmallToolbar>
                      <FormTile>
                        <NextJobForm
                          dataPipelineList={dataPipelineList}
                          nextJobToggle={nextJobState.nextJobToggle}
                          nextDataPipelineId={nextJobState.nextDataPipelineId}
                          nextJobId={nextJobState.nextJobId}
                          errorDataPipelineId={nextJobState.errorDataPipelineId}
                          errorJobId={nextJobState.errorJobId}
                          setNextJobState={setNextJobState}
                        />
                      </FormTile>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={jobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.dataMastering}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteJobState.confirmTitle,
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
    </CommonLayout>
  );
}
